/*
  mezuzi, theme 169.0 (#98b) — drag-to-dismiss on the phone cart sheet.

  Below 640 the source's cart is a bottom sheet (#98a, base.css) that follows a finger dragged down
  from its grip and closes on release past 30% of its height — the same ModalElement + gesture-element
  as its menu sheet. The numbers are the source's, as already copied for our menu sheet
  (assets/header-drawer.js, theme 92.0): the grip is the top 48px, the panel never leaves fully
  (height - 50), 30% to dismiss, `transform .1s linear` while the finger is down. Measured on the source
  13.10 (review-169/k/src98.mjs, sheet98.mjs): drag 400 closes, drag 100 snaps back in ~150 ms.

  Delegated from the document (passive), so a re-rendered cart needs no rebinding; it does nothing
  above 639px, on a closed cart, or for a finger that lands below the grip — scrolling the items list
  never moves the sheet. The dim follows the drag through --cart-sheet-dim (base.css, the ::backdrop
  inherits it). Closing goes through the drawer's own close(), so focus, scroll lock and the slide-out
  stay Tinker's; the inline styles are handed back on the dialog's `close` event.
*/

const GRIP_HEIGHT = 48;
const DRAG_TAIL = 50;
const DISMISS_FRACTION = 0.3;
const DRAG_TRANSITION = 'transform .1s linear';
const SETTLE_MS = 150;
const DIALOG = '#cart-drawer > dialog.cart-drawer__dialog';
const PHONE = window.matchMedia('(max-width: 639px)');

/** @param {number} value */
const clamp01 = (value) => Math.min(1, Math.max(0, value));

/** @type {{ dialog: HTMLDialogElement, startY: number, moveY: number, height: number, frame: number | null } | null} */
let drag = null;

/** @param {HTMLDialogElement} dialog */
function handBack(dialog) {
  dialog.style.transform = '';
  dialog.style.transition = '';
  dialog.style.removeProperty('--cart-sheet-dim');
  dialog.classList.remove('cart-drawer__dialog--dragging');
}

/** @param {TouchEvent} event */
function onTouchStart(event) {
  if (!PHONE.matches || !(event.target instanceof Element)) return;
  const dialog = /** @type {HTMLDialogElement | null} */ (event.target.closest(DIALOG));
  const touch = event.changedTouches[0];
  if (!dialog || !dialog.open || !touch) return;

  const box = dialog.getBoundingClientRect();
  // On the dim above the sheet the target is the dialog too (::backdrop hits it) - that is not the grip; the source does not move there.
  if (touch.clientY < box.top || touch.clientY - box.top > GRIP_HEIGHT) return;

  if (!dialog.dataset.sheetDragBound) {
    dialog.dataset.sheetDragBound = 'true';
    dialog.addEventListener('close', () => handBack(dialog));
  }
  drag = { dialog, startY: touch.screenY, moveY: 0, height: box.height, frame: null };
}

/** @param {TouchEvent} event */
function onTouchMove(event) {
  const current = drag;
  const touch = event.changedTouches[0];
  if (!current || !touch) return;

  current.moveY = touch.screenY - current.startY;
  if (current.frame !== null) return;

  current.frame = requestAnimationFrame(() => {
    current.frame = null;
    const { dialog } = current;
    dialog.classList.add('cart-drawer__dialog--dragging');

    if (current.moveY >= 0) {
      dialog.style.transition = DRAG_TRANSITION;
      dialog.style.transform = `translateY(${Math.min(current.moveY, current.height - DRAG_TAIL)}px)`;
      dialog.style.setProperty('--cart-sheet-dim', String(clamp01(1 - current.moveY / current.height)));
    } else {
      // Dragging up moves nothing on the source, and it drops the transition while it holds.
      dialog.style.transform = 'translateY(0)';
      dialog.style.transition = '';
      dialog.style.setProperty('--cart-sheet-dim', '1');
    }
  });
}

function onTouchEnd() {
  const current = drag;
  if (!current) return;
  drag = null;
  if (current.frame !== null) cancelAnimationFrame(current.frame);

  const { dialog } = current;
  if (current.moveY <= 0) {
    handBack(dialog);
    return;
  }

  dialog.style.transition = DRAG_TRANSITION;

  if (current.moveY < current.height * DISMISS_FRACTION) {
    dialog.style.transform = 'translateY(0)';
    // The source hands its overlay back to the stylesheet here, so the dim returns on its own .8s curve, not the drag's .1s.
    dialog.classList.remove('cart-drawer__dialog--dragging');
    dialog.style.removeProperty('--cart-sheet-dim');
    setTimeout(() => {
      if (!drag) handBack(dialog);
    }, SETTLE_MS);
    return;
  }

  dialog.style.transform = `translateY(${current.height}px)`;
  dialog.style.setProperty('--cart-sheet-dim', '0');
  setTimeout(() => {
    const host = /** @type {(HTMLElement & { close?: () => void }) | null} */ (dialog.closest('theme-drawer'));
    host?.close?.();
  }, SETTLE_MS);
}

document.addEventListener('touchstart', onTouchStart, { passive: true });
document.addEventListener('touchmove', onTouchMove, { passive: true });
document.addEventListener('touchend', onTouchEnd, { passive: true });
document.addEventListener('touchcancel', onTouchEnd, { passive: true });
